import { termMaxConfigByChain } from '@1delta/data-sdk'
import { MulticallRetryFunction } from '../../types/providers'
import { GenericTokenList } from '../../types/general'
import { toGenericPriceKey, toOracleKey } from '../../assets'
import { fetchTermMaxMarkets } from '../../lending/public-data/termmax/fetchPublic'
import { curatorNameFromVaultName } from '../curatorName'
import { Erc20DecimalsBalanceAbi, TermMaxVaultAbi } from './abi'
import type { TermMaxVault, TermMaxVaults } from './types'

/** 1e8 — TermMax's ratio base (`performanceFeeRate`, `apr`). */
const DECIMAL_BASE = 100_000_000n

const big = (v: any): bigint => {
  if (v === undefined || v === null || v === '0x') return 0n
  try {
    return typeof v === 'bigint' ? v : BigInt(v)
  } catch {
    return 0n
  }
}
const num = (v: any): number | undefined => {
  const n = Number(v)
  return Number.isFinite(n) ? n : undefined
}
const str = (v: any): string | undefined =>
  typeof v === 'string' && v.length > 0 ? v : undefined
const addr = (v: any): string | undefined =>
  typeof v === 'string' && /^0x[0-9a-fA-F]{40}$/.test(v) && !/^0x0{40}$/.test(v)
    ? v.toLowerCase()
    : undefined

/**
 * Discover the chain's TermMax vaults.
 *
 * There is NO vault-list endpoint, and the docs' `TMX-*` addresses are
 * near-empty seed vaults that miss every real one (the largest live vault,
 * Coinshift's rlUSD at ~$20.7M, is absent from that page). The reliable source
 * is the order book: a vault shows up as an order MAKER flagged
 * `makerIsVault`. We already fetch that data for the lender side, so discovery
 * is effectively free and always current.
 */
async function discoverVaultAddresses(chainId: string): Promise<string[]> {
  const markets = await fetchTermMaxMarkets(chainId).catch(() => [])
  const seen = new Set<string>()
  for (const m of markets) {
    for (const o of m.orders) {
      if (!o.makerIsVault) continue
      const maker = addr(o.makerAddress)
      if (maker) seen.add(maker)
    }
  }
  return [...seen]
}

const VAULT_READS = [
  'name',
  'symbol',
  'decimals',
  'asset',
  'totalAssets',
  'totalSupply',
  'annualizedInterest',
  'accretingPrincipal',
  'performanceFeeRate',
  'apr',
  'curator',
  'guardian',
  'timelock',
  'getVersion',
] as const
const READS_PER_VAULT = VAULT_READS.length

/**
 * ON-CHAIN FALLBACK for `fetchFromApi.ts`. Used when `/vault/list` is
 * unavailable; the API is preferred because it also carries curator NAMES,
 * caps, rewards and the base pool, none of which are cheaply readable here.
 *
 * These are the CONTINUOUS earn side of a fixed-maturity protocol: the vault
 * holds a rolling book of maker orders across maturities and rolls them for the
 * depositor, so it presents as an ordinary ERC-4626 vault. Modeled like the
 * Morpho / Silo / Euler-Earn multi-curator providers.
 */
export const fetchTermMaxVaultsFromChain = async (
  chainId: string,
  multicallRetry: MulticallRetryFunction,
  prices: { [asset: string]: number } = {},
  tokenList: GenericTokenList = {},
): Promise<TermMaxVaults> => {
  if (!termMaxConfigByChain(chainId)) return {}

  const vaults = await discoverVaultAddresses(chainId)
  if (vaults.length === 0) return {}

  // Round 1 — vault state. `allowFailure` matters here: `apr()` and
  // `accretingPrincipal()` revert on the deployed 2.0.0 vaults.
  const calls = vaults.flatMap((address) =>
    VAULT_READS.map((name) => ({
      address,
      name,
      params: [],
    })),
  )
  const res = (await multicallRetry({
    chain: chainId,
    calls,
    abi: TermMaxVaultAbi as any,
    allowFailure: true,
  })) as any[]

  type Row = {
    address: string
    name?: string
    symbol?: string
    decimals: number
    asset?: string
    totalAssets: bigint
    totalSupply: bigint
    annualizedInterest: bigint
    accretingPrincipal: bigint
    performanceFeeRate: bigint
    aprRaw?: bigint
    curator?: string
    guardian?: string
    timelock?: number
    version?: string
  }

  const rows: Row[] = []
  vaults.forEach((address, i) => {
    const at = (k: (typeof VAULT_READS)[number]) =>
      res[i * READS_PER_VAULT + VAULT_READS.indexOf(k)]
    const asset = addr(at('asset'))
    const totalAssets = big(at('totalAssets'))
    // A vault with no asset or no assets is a stub — drop it rather than
    // publishing an empty row. (Many chains carry never-funded vault makers.)
    if (!asset || totalAssets === 0n) return
    rows.push({
      address,
      name: str(at('name')),
      symbol: str(at('symbol')),
      decimals: num(at('decimals')) ?? 18,
      asset,
      totalAssets,
      totalSupply: big(at('totalSupply')),
      annualizedInterest: big(at('annualizedInterest')),
      accretingPrincipal: big(at('accretingPrincipal')),
      performanceFeeRate: big(at('performanceFeeRate')),
      aprRaw: at('apr') != null ? big(at('apr')) : undefined,
      curator: addr(at('curator')),
      guardian: addr(at('guardian')),
      timelock: num(at('timelock')),
      version: str(at('getVersion')),
    })
  })
  if (rows.length === 0) return {}

  // Round 2 — underlying decimals + the vault's idle balance (the honest
  // withdrawable floor: the rest of the capital sits in maker orders until
  // maturity).
  const round2 = rows.flatMap((r) => [
    { address: r.asset!, name: 'decimals', params: [] },
    { address: r.asset!, name: 'balanceOf', params: [r.address] },
  ])
  const res2 = (await multicallRetry({
    chain: chainId,
    calls: round2,
    abi: Erc20DecimalsBalanceAbi as any,
    allowFailure: true,
  })) as any[]

  const out: TermMaxVaults = {}
  rows.forEach((r, i) => {
    const assetDecimals = num(res2[i * 2]) ?? r.decimals
    const idle = big(res2[i * 2 + 1])

    const assetEntry = tokenList[r.asset!]
    const priceKey =
      toOracleKey(assetEntry?.assetGroup!) || toGenericPriceKey(r.asset!, chainId)
    const priceUsd = prices[priceKey]

    // Net supply APR. TermMax's own formula (OrderManagerV2):
    //   annualizedInterest · (1e8 − performanceFeeRate) / accretingPrincipal
    // `annualizedInterest` is GROSS — the fee is deducted on accrual — so it
    // must be netted. `accretingPrincipal` reverts on 2.0.0 vaults, in which
    // case `totalAssets` stands in: a larger denominator, so the rate is
    // understated rather than overstated.
    const denominator =
      r.accretingPrincipal > 0n ? r.accretingPrincipal : r.totalAssets
    const feeRate = r.performanceFeeRate
    let supplyRate = 0
    if (denominator > 0n && r.annualizedInterest > 0n) {
      const netAnnual =
        (r.annualizedInterest * (DECIMAL_BASE - feeRate)) / DECIMAL_BASE
      supplyRate = (Number(netAnnual) / Number(denominator)) * 100
    }
    // Prefer the contract's own figure when it is readable (newer deployments).
    if (r.aprRaw !== undefined && r.aprRaw > 0n) {
      supplyRate = (Number(r.aprRaw) / Number(DECIMAL_BASE)) * 100
    }

    const scale = 10 ** assetDecimals
    const totalAssetsFormatted = Number(r.totalAssets) / scale
    const liquidityFormatted = Number(idle) / scale
    const convertToAssets =
      r.totalSupply > 0n
        ? ((r.totalAssets * 10n ** BigInt(r.decimals)) / r.totalSupply).toString()
        : '0'

    const entry: TermMaxVault = {
      address: r.address,
      underlying: r.asset!,
      symbol: r.symbol ?? '',
      name: r.name ?? '',
      decimals: r.decimals,
      assetDecimals,
      totalAssets: r.totalAssets.toString(),
      totalSupply: r.totalSupply.toString(),
      convertToAssets,
      supplyRate,
      rewardsRate: 0,
      depositRate: supplyRate,
      fee: (Number(feeRate) / Number(DECIMAL_BASE)) * 100,
      timelock: r.timelock,
      curator: r.curator,
      curatorName: curatorNameFromVaultName(r.name),
      guardian: r.guardian,
      asset: assetEntry,
      priceUsd,
      totalAssetsFormatted,
      totalAssetsUsd: priceUsd ? totalAssetsFormatted * priceUsd : 0,
      liquidity: idle.toString(),
      liquidityFormatted,
      liquidityUsd: priceUsd ? liquidityFormatted * priceUsd : 0,
      version: r.version,
    } as TermMaxVault

    out[r.address] = entry
  })

  return out
}
